<script lang="ts" setup>
    import '../../../css/choice.css';  
    import {onMounted,computed,reactive} from 'vue';  
    import CodeValidationSearch from './CodeValidationSearch.vue'; 
    import ModalDialog from '../../components/ModalDialog.vue';  
    import Icon from "../../components/Icon.vue";  
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import FloatInput from '../../components/FloatInput.vue';
    import RadioButtonInput from "../../components/RadioButtonInput.vue";
    import {valdiationRule} from '../../lib/Models/CodeValidationCopyModel'; 
    import {LayoutOrientation,ModalSize,IconTypes,DisplayStyle} from "../../Enums/UiEnums"; 
    import {initPinia} from '../../lib/PiniaMgr';
    import {useCodeValidationCopyStore} from '../../lib/Stores/CodeValidationCopyStore';

    const codeValidationCopyStore = useCodeValidationCopyStore(initPinia()); 
    const formData = reactive <
    {   
        showSearch: boolean;
        bypass: boolean, 
        providerValue: string | undefined,
        minUnitValue: number | undefined,
        maxUnitValue: number | undefined
    }>({
        showSearch: false,
        bypass: false,
        providerValue: "",
        minUnitValue: undefined,
        maxUnitValue: undefined,
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        validationID: number,
    }>(),
    {
        saveEnabled: 0,
        validationID: 0
    });
    onMounted(async () => {  
        if (props.validationID > valdiationRule.Header) {
            formData.bypass = codeValidationCopyStore.getBypassValue(props.validationID);
            formData.providerValue = codeValidationCopyStore.getProviderValue();
            formData.minUnitValue = codeValidationCopyStore.getMinUnitValue();
            formData.maxUnitValue = codeValidationCopyStore.getMaxUnitValue();
        }
    });  
    const valueCount = computed<number>(() => {
        if (props.validationID > valdiationRule.Header) {
            return codeValidationCopyStore.getValueCount(props.validationID);
        }
        return 0;
    });
    const inputDisabled = computed<boolean>(() => {
        if (props.validationID >= valdiationRule.MinUnits) {
            return codeValidationCopyStore.getDisabled(props.validationID)
        }
        return false;
    });
    const addIconTitle = computed<string>(()=> {
        if (props.validationID >= valdiationRule.MinUnits)
            return "";
        if (props.validationID <= valdiationRule.ModRestricted)
            return "Edit list of Modifiers";
        if (props.validationID <= valdiationRule.PcRestricted)
            return "Edit list of Procedures";
        return "Edit list of Diagnoses";
    }); 

    function editValues() : void {
        formData.showSearch = true;
    }
    function deleteValues() : void {
        codeValidationCopyStore.deleteAllValues(props.validationID);
    }
    function onChangeBypass() : void {
        codeValidationCopyStore.setBypassValue(props.validationID,formData.bypass);
    }
    function onChangeProviderValue() : void {
        codeValidationCopyStore.setProviderValue(formData.providerValue);
    }
    function onChangeMinUnitsValue() : void {
        codeValidationCopyStore.setMinUnitValue(formData.minUnitValue);
    }
    function onChangeMaxUnitsValue() : void {
        codeValidationCopyStore.setMaxUnitValue(formData.maxUnitValue);
    }
</script> 

<style scoped> 
    .data-cell {
      FONT-SIZE: .75rem;
      font-family: Arial, sans-serif, Helvetica;
      padding: 2px 2px 2px 2px;
      cursor: default;
      vertical-align: top;
    }
</style>
<template>
    <table class="label-isalus-small" style="table-layout: fixed; width: 460px;">
        <colgroup>
            <col style="width: 25px;"/>
            <col style="width: 25px;"/>
            <col style="width: 335px;"/>
            <col style="width: 85px;"/>
        </colgroup>
        <tr v-if="props.validationID == valdiationRule.ModRequired">
            <td class="text-isalus-primary" colspan="2">Bypass</td>
            <td class="text-isalus-primary">Description</td>
            <td class="text-isalus-primary">#Values</td>
        </tr>
        <tr v-if="props.validationID >= valdiationRule.ModRequired">
            <td>{{props.validationID }}.</td>
            <td> 
                <Checkbox v-model="formData.bypass" @update:modelValue="onChangeBypass" :title="'Allow Bypass'"/>
            </td>
            <td>
                <span v-if="props.validationID == valdiationRule.ModRequired"> At least one of the following modifiers must be used.</span>
                <span v-if="props.validationID == valdiationRule.ModRestricted">None of the following modifiers must be used.</span>
                <span v-if="props.validationID == valdiationRule.PcRequired">At least one of the following procedures must be used.</span>
                <span v-if="props.validationID == valdiationRule.PcRestricted">None of the following procedures must be used.</span>
                <span v-if="props.validationID == valdiationRule.DxRequired">At least one of the following diagnoses must be used.</span>
                <span v-if="props.validationID == valdiationRule.DxRestricted">None of the following diagnoses must be used.</span>
                <span v-if="props.validationID == valdiationRule.PdxRequired">At least one of the following primary diagnoses must be used.</span>
                <span v-if="props.validationID == valdiationRule.PdxRestricted">None of the following primary diagnoses must be used.</span>
                <span v-if="props.validationID == valdiationRule.MinUnits">Minimum Units.</span>
                <span v-if="props.validationID == valdiationRule.MaxUnits">Maximum Units.</span>
                <span v-if="props.validationID == valdiationRule.RenderingReferring">Rendering and Referring Providers.</span>
            </td>
            <td v-if="props.validationID < valdiationRule.MinUnits"> 
                <span v-if="props.saveEnabled == 1">
                    <Icon 
                        :icon="IconTypes.Edit" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-small text-isalus-primary'"
                        :title="addIconTitle"
                        @click="editValues"/>
                </span>
                <span v-if="props.saveEnabled == 1 && codeValidationCopyStore.codeValidationResults.filter(item => item.ruleId == props.validationID && item.ruleValue == '').length == 0" class="ps-2">
                    <Icon 
                        :icon="IconTypes.Delete" 
                        :displayStyle="DisplayStyle.Danger" 
                        :class="'label-isalus-small'"
                        :title="'Remove all validations'"
                        @click="deleteValues"/>
                </span>
                <span class="ps-2">
                    {{ (valueCount == 0 ? "" : valueCount) }}  
                </span>
            </td>
            <td v-if="props.validationID >= valdiationRule.MinUnits"> 
                &nbsp;
            </td>
        </tr>
        <tr v-if="props.validationID == valdiationRule.MinUnits">
            <td colspan="2">&nbsp;</td>
            <td colspan="2">
                <div style="width: 4rem;">
                    <FloatInput 
                        v-model:numberValue="formData.minUnitValue"
                        @change:numberValue="onChangeMinUnitsValue"
                        :maxLength="10"
                        :disabled="inputDisabled"
                        :startingRange="0"
                        :endingRange="999.99"
                        :minPrecision="2"
                        :maxPrecision="2"
                        :positive="true"
                        :errorDisplay="false"
                        :cssFramework="'vanilla'"
                        :placeholder="'Units'"
                    />
                </div>
            </td>
        </tr>
        <tr v-if="props.validationID == valdiationRule.MaxUnits">
            <td colspan="2">&nbsp;</td>
            <td colspan="2">
                <div style="width: 4rem;">
                    <FloatInput 
                        v-model:numberValue="formData.maxUnitValue"
                        @change:numberValue="onChangeMaxUnitsValue"
                        :maxLength="10"
                        :disabled="inputDisabled"
                        :startingRange="0"
                        :endingRange="999.99"
                        :minPrecision="2"
                        :maxPrecision="2"
                        :positive="true"
                        :errorDisplay="false"
                        :cssFramework="'vanilla'"
                        :placeholder="'Units'"
                    />
                </div>
            </td>
        </tr>
        <tr v-if="props.validationID == valdiationRule.RenderingReferring">
            <td colspan="2">&nbsp;</td>
            <td colspan="2">
                <RadioButtonInput value="0" :disabled="inputDisabled" v-model="formData.providerValue" @change="onChangeProviderValue" :layout="LayoutOrientation.Horz">N/A</RadioButtonInput>
                <RadioButtonInput value="1" :disabled="inputDisabled" v-model="formData.providerValue" @change="onChangeProviderValue" :layout="LayoutOrientation.Horz">Must be the Same</RadioButtonInput>
                <RadioButtonInput value="2" :disabled="inputDisabled" v-model="formData.providerValue" @change="onChangeProviderValue" :layout="LayoutOrientation.Horz">Cannot be the same</RadioButtonInput>
            </td>
        </tr>
    </table>
    <ModalDialog 
        v-model:show="formData.showSearch"
        :size="ModalSize.Large"
        :closeOnClickOutside="false"
        >
        <template #title>
            <div class="text-isalus-primary">Edit Code Validations</div>
        </template> 
        <CodeValidationSearch
            :validationID="props.validationID"
        /> 
    </ModalDialog> 
</template>
